import { memo, useState, useEffect, useMemo, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../auth/AuthProvider'
import { useCurrentUser } from '../../hooks/api/useUsers'
import { hasAnyEntityRole } from '../../utils/permissions'
import {
  Home,
  Building,
  Users,
  Wallet,
  Settings,
  LogOut,
  Plus,
  ChevronUp,
  PanelLeftOpen,
  FilePen,
  Workflow,
  Landmark,
  FileText,
  BookOpen,
  MessageSquare,
  LayoutTemplate,
  Settings2,
  ClipboardCheck,
  ShieldCheck,
  Sun,
  Moon,
  Monitor
} from 'lucide-react'
import { useAppStore } from '../../store'
import { useMyPendingTasksCount } from '../../hooks/api/useMyPendingTasksCount'
import { useIsAdmin } from '../../hooks/useIsAdmin'
import {
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarItem,
  SidebarTrigger,
  useSidebar
} from '../ui/sidebar'
import { CollapsibleSidebarGroup } from '../ui/CollapsibleSidebarGroup'
import { Button } from '../ui/Button'
import { Avatar } from '../ui/Avatar'
import { cn } from '../../lib/utils'
import { AppLanguageSwitcher } from './AppLanguageSwitcher'
import { ContactDialog } from '../other/ContactDialog'

// Flat navigation - ordered by usage frequency (Linear-style)
const navigation = [
  { href: '/home', labelKey: 'primary.home', icon: Home },
  { href: '/mandate-history', labelKey: 'primary.mandates', icon: FilePen },
  { href: '/entities', labelKey: 'primary.entities', icon: Building },
  { href: '/bank-accounts', labelKey: 'primary.bankAccounts', icon: Wallet },
  { href: '/signatories', labelKey: 'primary.signatories', icon: Users },
  { href: '/kyc', labelKey: 'primary.kyc', icon: ShieldCheck },
]

const settingsNavigation = [
  { href: '/banks', labelKey: 'settings.banks', icon: Landmark },
  { href: '/users', labelKey: 'settings.users', icon: Users },
  { href: '/workflows', labelKey: 'settings.workflows', icon: Workflow },
  { href: '/settings/mandate-templates', labelKey: 'settings.mandateTemplates', icon: LayoutTemplate, adminOnly: true },
  { href: '/settings/organisation', labelKey: 'settings.organisation', icon: Settings2, adminOnly: true },
  { href: '/reviews', labelKey: 'settings.periodicReviews', icon: ClipboardCheck, adminOnly: true },
  { href: '/ebam', labelKey: 'settings.ebam', icon: FileText, adminOnly: true },
]

function AppSidebarComponent() {
  const navigate = useNavigate()
  const location = useLocation()
  const { t, i18n } = useTranslation('nav')
  const { t: tCommon } = useTranslation('common')
  const { t: tContact } = useTranslation('contact')
  const { collapsed, setCollapsed} = useSidebar()
  const { signOut } = useAuth()
  const { data: user } = useCurrentUser()
  const canCreateMandate = hasAnyEntityRole(user, 'contributor')
  const { isAdmin } = useIsAdmin()
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [contactOpen, setContactOpen] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)
  const theme = useAppStore(state => state.theme)
  const setTheme = useAppStore(state => state.setTheme)

  // Legal pages live on the public site; open them in a new tab, in the
  // viewer's language, so the app session stays put.
  const legalLang = (i18n.language || 'fr').toLowerCase().startsWith('en') ? 'en' : 'fr'

  // Close user menu when clicking outside
  useEffect(() => {
    if (!userMenuOpen) return

    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [userMenuOpen])

  // Count of tasks assigned to the current user, aligned with the
  // "My Tasks" card on HomePage
  const pendingTasksCount = useMyPendingTasksCount()

  // Filter settings navigation based on admin status
  const filteredSettingsNavigation = useMemo(
    () => settingsNavigation.filter(item => !item.adminOnly || isAdmin),
    [isAdmin]
  )

  // Determine active item based on current route
  const isActive = (href: string) => {
    if (href === '/home') {
      return location.pathname === '/' ||
             location.pathname === '/home' ||
             location.pathname.startsWith('/home/')
    }
    if (href === '/mandate-history') {
      return location.pathname.startsWith('/mandate-history') ||
             location.pathname.startsWith('/mandates')
    }
    return location.pathname.startsWith(href)
  }
  
  // Keyboard shortcut for sidebar toggle (Cmd+B / Ctrl+B)
  useEffect(() => {
    const handleKeyboard = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'b') {
        e.preventDefault()
        setCollapsed(!collapsed)
      }
    }

    window.addEventListener('keydown', handleKeyboard)
    return () => window.removeEventListener('keydown', handleKeyboard)
  }, [collapsed, setCollapsed])

  return (
    <Sidebar>
      <SidebarHeader>
        <div className={cn(
          "flex items-center justify-between h-12 px-2",
          collapsed && "px-0"
        )}>
          <button
            onClick={() => setCollapsed(!collapsed)}
            className={cn(
              "flex items-center gap-2 rounded-md px-2 py-1.5 transition-all hover:bg-accent/20",
              collapsed && "w-full justify-center px-2"
            )}
            title={collapsed ? t('sidebar.expand') : t('sidebar.collapse')}
          >
            {collapsed ? (
              /* Icon only when collapsed */
              <svg
                className="h-5 w-5"
                viewBox="0 0 45 60"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M0 4V4C8.28427 4 15 10.7157 15 19V54V54C6.71573 54 0 47.2843 0 39V4Z" fill="#5E6AD2"/>
                <path d="M36.9813 1V1C42.3064 7.34612 41.4786 16.8075 35.1325 22.1325L29.3872 26.9534C26.2141 29.6159 21.4834 29.202 18.8209 26.029V26.029C16.1584 22.8559 16.5723 18.1252 19.7453 15.4627L36.9813 1Z" fill="url(#paint0_linear_sidebar)"/>
                <path d="M19.7453 43.3116C16.5723 40.6491 16.1584 35.9184 18.8209 32.7453V32.7453C21.4834 29.5723 26.2141 29.1584 29.3871 31.8209L35.1325 36.6418C41.4786 41.9668 42.3064 51.4282 36.9813 57.7743V57.7743L19.7453 43.3116Z" fill="url(#paint1_linear_sidebar)"/>
                <defs>
                  <linearGradient id="paint0_linear_sidebar" x1="41.8022" y1="6.74533" x2="18.8209" y2="26.029" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#7078DA"/>
                    <stop offset="1" stopColor="#5E6AD2"/>
                  </linearGradient>
                  <linearGradient id="paint1_linear_sidebar" x1="18.8209" y1="32.7453" x2="41.8022" y2="52.029" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#5E6AD2"/>
                    <stop offset="1" stopColor="#5864C6"/>
                  </linearGradient>
                </defs>
              </svg>
            ) : (
              /* Full logo when expanded */
              <svg
                className="h-6 w-auto text-foreground"
                viewBox="0 0 180 62"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M0 4V4C8.28427 4 15 10.7157 15 19V54V54C6.71573 54 0 47.2843 0 39V4Z" fill="#5E6AD2"/>
                <path d="M36.9813 1V1C42.3064 7.34612 41.4786 16.8075 35.1325 22.1325L29.3872 26.9534C26.2141 29.6159 21.4834 29.202 18.8209 26.029V26.029C16.1584 22.8559 16.5723 18.1252 19.7453 15.4627L36.9813 1Z" fill="url(#paint0_linear_sidebar_full)"/>
                <path d="M19.7453 43.3116C16.5723 40.6491 16.1584 35.9184 18.8209 32.7453V32.7453C21.4834 29.5723 26.2141 29.1584 29.3871 31.8209L35.1325 36.6418C41.4786 41.9668 42.3064 51.4282 36.9813 57.7743V57.7743L19.7453 43.3116Z" fill="url(#paint1_linear_sidebar_full)"/>
                <path d="M61.36 48V13.92H67.504V33.936L78.016 22.368H85.6L75.568 33.024L85.936 48H79.024L71.536 36.672L67.504 40.944V48H61.36ZM95.0162 48.576C92.3282 48.576 90.1682 47.968 88.5362 46.752C86.9042 45.504 86.0882 43.776 86.0882 41.568C86.0882 39.36 86.7762 37.632 88.1522 36.384C89.5282 35.136 91.6242 34.24 94.4403 33.696L102.936 32.016C102.936 30.192 102.52 28.832 101.688 27.936C100.856 27.008 99.6243 26.544 97.9922 26.544C96.5202 26.544 95.3523 26.896 94.4883 27.6C93.6562 28.272 93.0802 29.248 92.7602 30.528L86.5202 30.24C87.0322 27.52 88.2802 25.44 90.2642 24C92.2482 22.528 94.8242 21.792 97.9922 21.792C101.64 21.792 104.392 22.72 106.248 24.576C108.136 26.4 109.08 29.024 109.08 32.448V41.76C109.08 42.432 109.192 42.896 109.416 43.152C109.672 43.408 110.04 43.536 110.52 43.536H111.336V48C111.144 48.064 110.824 48.112 110.376 48.144C109.96 48.176 109.528 48.192 109.08 48.192C108.024 48.192 107.08 48.032 106.248 47.712C105.416 47.36 104.776 46.768 104.328 45.936C103.88 45.072 103.656 43.904 103.656 42.432L104.184 42.816C103.928 43.936 103.368 44.944 102.504 45.84C101.672 46.704 100.616 47.376 99.3363 47.856C98.0563 48.336 96.6162 48.576 95.0162 48.576ZM96.2643 44.112C97.6402 44.112 98.8242 43.84 99.8163 43.296C100.808 42.752 101.576 42 102.12 41.04C102.664 40.08 102.936 38.944 102.936 37.632V36.192L96.3122 37.536C94.9362 37.824 93.9442 38.256 93.3363 38.832C92.7602 39.376 92.4722 40.096 92.4722 40.992C92.4722 41.984 92.7922 42.752 93.4322 43.296C94.1042 43.84 95.0483 44.112 96.2643 44.112ZM127.381 48.576C125.589 48.576 124.021 48.192 122.677 47.424C121.365 46.656 120.341 45.584 119.605 44.208L119.461 48H113.605V13.92H119.749V26.016C120.453 24.832 121.461 23.84 122.773 23.04C124.085 22.208 125.621 21.792 127.381 21.792C129.589 21.792 131.493 22.352 133.093 23.472C134.725 24.56 135.973 26.112 136.837 28.128C137.733 30.112 138.181 32.464 138.181 35.184C138.181 37.904 137.733 40.272 136.837 42.288C135.973 44.272 134.725 45.824 133.093 46.944C131.493 48.032 129.589 48.576 127.381 48.576ZM125.989 43.584C127.749 43.584 129.157 42.848 130.213 41.376C131.269 39.872 131.797 37.808 131.797 35.184C131.797 32.528 131.269 30.464 130.213 28.992C129.189 27.52 127.797 26.784 126.037 26.784C124.725 26.784 123.589 27.12 122.629 27.792C121.701 28.432 120.981 29.376 120.469 30.624C119.989 31.872 119.749 33.392 119.749 35.184C119.749 36.912 119.989 38.416 120.469 39.696C120.981 40.944 121.701 41.904 122.629 42.576C123.557 43.248 124.677 43.584 125.989 43.584ZM147.887 48C146.031 48 144.543 47.52 143.423 46.56C142.303 45.6 141.743 44.064 141.743 41.952V13.92H147.887V41.328C147.887 41.968 148.047 42.448 148.367 42.768C148.719 43.088 149.199 43.248 149.807 43.248H151.679V48H147.887ZM165.266 48.576C162.706 48.576 160.482 48.032 158.594 46.944C156.706 45.824 155.25 44.256 154.226 42.24C153.202 40.224 152.69 37.872 152.69 35.184C152.69 32.496 153.202 30.16 154.226 28.176C155.25 26.16 156.69 24.592 158.546 23.472C160.434 22.352 162.626 21.792 165.122 21.792C167.554 21.792 169.682 22.336 171.506 23.424C173.362 24.512 174.786 26.08 175.778 28.128C176.77 30.176 177.266 32.64 177.266 35.52V36.912H159.074C159.202 39.152 159.81 40.848 160.898 42C162.018 43.12 163.49 43.68 165.314 43.68C166.69 43.68 167.826 43.376 168.722 42.768C169.65 42.128 170.29 41.248 170.642 40.128L176.93 40.512C176.226 43.008 174.834 44.976 172.754 46.416C170.706 47.856 168.21 48.576 165.266 48.576ZM159.074 32.784H170.93C170.802 30.704 170.21 29.168 169.154 28.176C168.098 27.152 166.754 26.64 165.122 26.64C163.49 26.64 162.13 27.168 161.042 28.224C159.986 29.28 159.33 30.8 159.074 32.784Z" fill="currentColor"/>
                <defs>
                  <linearGradient id="paint0_linear_sidebar_full" x1="41.8022" y1="6.74533" x2="18.8209" y2="26.029" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#7078DA"/>
                    <stop offset="1" stopColor="#5E6AD2"/>
                  </linearGradient>
                  <linearGradient id="paint1_linear_sidebar_full" x1="18.8209" y1="32.7453" x2="41.8022" y2="52.029" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#5E6AD2"/>
                    <stop offset="1" stopColor="#5864C6"/>
                  </linearGradient>
                </defs>
              </svg>
            )}
          </button>
          {!collapsed && (
            <SidebarTrigger className="mr-0" />
          )}
        </div>
      </SidebarHeader>

      {/* Floating expand button - only visible when collapsed */}
      {collapsed && (
        <button
          onClick={() => setCollapsed(false)}
          className={cn(
            "sidebar-expand-button",
            "absolute top-3 -right-3 z-50",
            "flex h-6 w-6 items-center justify-center",
            "rounded-full border border-border",
            "bg-background text-muted-foreground",
            "shadow-md hover:shadow-lg",
            "transition-all duration-200 ease-out",
            "hover:scale-110 hover:text-foreground",
            "active:scale-95",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          )}
          title={t('sidebar.expand')}
          aria-label={t('sidebar.expandAriaLabel')}
        >
          <PanelLeftOpen className="h-3.5 w-3.5" />
        </button>
      )}
      
      <SidebarContent>
        {/* Main navigation - flat structure without sections */}
        <SidebarGroup>
          {navigation.map((item) => {
            const Icon = item.icon
            const active = isActive(item.href)
            const label = t(item.labelKey)

            return (
              <SidebarItem
                key={item.labelKey}
                href={item.href}
                active={active}
                onClick={(e) => {
                  e.preventDefault()
                  navigate(item.href)
                }}
                icon={<Icon className="h-4 w-4" />}
                badge={
                  item.href === '/home' && pendingTasksCount > 0 ? (
                    <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary px-1.5 text-11 font-medium text-primary-foreground">
                      {pendingTasksCount}
                    </span>
                  ) : null
                }
              >
                {label}
              </SidebarItem>
            )
          })}
        </SidebarGroup>

        {/* Settings Collapsible Section */}
        <CollapsibleSidebarGroup label={t('settingsGroup')} icon={Settings} defaultOpen={false}>
          {filteredSettingsNavigation.map((item) => {
            const Icon = item.icon
            const active = isActive(item.href)
            const label = t(item.labelKey)

            return (
              <SidebarItem
                key={item.labelKey}
                href={item.href}
                active={active}
                onClick={(e) => {
                  e.preventDefault()
                  navigate(item.href)
                }}
                icon={<Icon className="h-4 w-4" />}
              >
                {label}
              </SidebarItem>
            )
          })}
        </CollapsibleSidebarGroup>

        {/* Primary CTA: below Settings. It is the sidebar's only CTA, so it stays
            prominent even here, and no longer sits oddly above the Settings group.
            Only for users who can create mandates. */}
        {canCreateMandate && (
          <div className={cn("mt-2", collapsed ? "px-1" : "px-2")}>
            <Button
              variant="default"
              className={cn(
                "w-full text-13 font-medium shadow-none hover:shadow-sm",
                collapsed && "px-2"
              )}
              onClick={() => navigate('/mandates/new')}
            >
              <Plus className="h-4 w-4" />
              {!collapsed && <span className="ml-2">{t('cta.newMandate')}</span>}
            </Button>
          </div>
        )}
      </SidebarContent>
      
      <SidebarFooter>
        <div className="relative" ref={userMenuRef}>
          <button
            className={cn(
              "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-13 font-normal transition-all hover:bg-accent/20",
              userMenuOpen && "bg-accent/20",
              collapsed && "justify-center px-2"
            )}
            onClick={() => setUserMenuOpen(!userMenuOpen)}
          >
            <Avatar
              variant="soft"
              name={user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'U' : 'U'}
              size={24}
              className="ring-1 ring-border/50"
            />
            {!collapsed && (
              <>
                <div className="flex-1 text-left">
                  <div className="text-13 font-medium">
                    {user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || tCommon('userMenu.fallbackName') : tCommon('userMenu.fallbackName')}
                  </div>
                  <div className="text-11 text-muted-foreground">
                    {user?.email || tCommon('userMenu.fallbackEmail')}
                  </div>
                </div>
                <ChevronUp className={cn(
                  "h-4 w-4 text-muted-foreground transition-transform",
                  userMenuOpen && "rotate-180"
                )} />
              </>
            )}
          </button>

          {/* User dropdown menu */}
          {userMenuOpen && (
            <div className={cn(
              "absolute z-50 w-full rounded-md border bg-popover/95 backdrop-blur-sm p-1 shadow-[0_8px_16px_rgba(0,0,0,0.12)]",
              collapsed ? "bottom-0 left-full ml-2 w-64" : "bottom-full mb-2"
            )}>
              <div className="flex items-center justify-between px-2 py-1.5 text-13">
                <span className="text-muted-foreground">{t('userMenu.language')}</span>
                <AppLanguageSwitcher />
              </div>
              <div className="flex items-center justify-between px-2 py-1.5 text-13">
                <span className="text-muted-foreground">{t('userMenu.theme')}</span>
                <div className="flex items-center gap-0.5 rounded-md border border-border p-0.5">
                  {([
                    { value: 'light', icon: Sun },
                    { value: 'dark', icon: Moon },
                    { value: 'system', icon: Monitor }
                  ] as const).map(({ value, icon: Icon }) => (
                    <button
                      key={value}
                      onClick={() => setTheme(value)}
                      aria-label={t(`userMenu.themeOptions.${value}`)}
                      aria-pressed={theme === value}
                      title={t(`userMenu.themeOptions.${value}`)}
                      className={cn(
                        "flex h-5 w-6 items-center justify-center rounded-sm transition-colors",
                        theme === value
                          ? "bg-muted text-foreground"
                          : "text-muted-foreground hover:text-foreground"
                      )}
                    >
                      <Icon className="h-3 w-3" />
                    </button>
                  ))}
                </div>
              </div>
              <div className="h-px bg-border my-1" />
              <button
                className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-13 font-normal hover:bg-accent/20"
                onClick={() => {
                  setUserMenuOpen(false)
                  navigate('/help')
                }}
              >
                <BookOpen className="h-4 w-4" />
                {t('userMenu.documentation')}
              </button>
              <button
                className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-13 font-normal hover:bg-accent/20"
                onClick={() => {
                  setUserMenuOpen(false)
                  setContactOpen(true)
                }}
              >
                <MessageSquare className="h-4 w-4" />
                {tContact('trigger')}
              </button>
              <div className="h-px bg-border my-1" />
              <button
                className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-13 font-normal hover:bg-accent/20"
                onClick={() => {
                  setUserMenuOpen(false)
                  void signOut().then(() => { window.location.href = '/' })
                }}
              >
                <LogOut className="h-4 w-4" />
                {t('userMenu.signOut')}
              </button>
              <div className="h-px bg-border my-1" />
              {/* Discreet legal fine print: opens the public pages in a new tab. */}
              <div className="flex flex-wrap items-center justify-center gap-x-1.5 gap-y-0.5 px-2 py-1.5 text-11 text-muted-foreground">
                <a
                  href={`/${legalLang}/privacy`}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => setUserMenuOpen(false)}
                  className="hover:text-foreground transition-colors"
                >
                  {t('userMenu.legal.privacy')}
                </a>
                <span aria-hidden="true">·</span>
                <a
                  href={`/${legalLang}/legal`}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => setUserMenuOpen(false)}
                  className="hover:text-foreground transition-colors"
                >
                  {t('userMenu.legal.notice')}
                </a>
                <span aria-hidden="true">·</span>
                <a
                  href={`/${legalLang}/terms`}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => setUserMenuOpen(false)}
                  className="hover:text-foreground transition-colors"
                >
                  {t('userMenu.legal.terms')}
                </a>
              </div>
            </div>
          )}
        </div>
      </SidebarFooter>

      <ContactDialog opened={contactOpen} onClose={() => setContactOpen(false)} />
    </Sidebar>
  )
}

export const AppSidebar = memo(AppSidebarComponent)